/**
 * Onboarding & first-time-experience layer — see docs/GAME_DESIGN.md §12.
 *
 * Four optional, polished aids rendered on top of the HUD:
 *  A. a first-run intro overlay (modal over the title screen);
 *  B. non-blocking contextual hint toasts during waves 1–2;
 *  C. tower tooltips on hover (HUD buttons + placed towers);
 *  D. a `?` / `H` Help panel.
 *
 * This layer is **UI-only**. Aids A–B never touch `GameState` and are gated by
 * `config.onboardingEnabled` plus `localStorage` flags; the contextual hints in
 * particular never pause or block the simulation. The Help panel pauses the
 * game through a renderer-side gate in `Game` (it never mutates `state`).
 * Tooltips and the Help panel are always available.
 */
import {
  CONFIG,
  type AbilityId,
  type ArcLevel,
  type CryoLevel,
  type EnemyType,
  type MortarLevel,
  type PylonLevel,
  type TowerType,
} from '../config';
import { clamp, roundRect, withAlpha } from '../engine/util';
import { t } from '../i18n';
import {
  isAbilityUnlocked,
  saveProfile,
  type ContextHintId,
  type Profile,
} from '../game/profile';
import type { GameState } from '../game/state';
import { abilityUnlockCost, hudLayout, towerUnlockCost } from './hud';

const C = CONFIG.colors;
const FONT_DISPLAY = '"Chakra Petch", "Rajdhani", system-ui, sans-serif';
const FONT_MONO = '"Share Tech Mono", ui-monospace, "DejaVu Sans Mono", monospace';
const HUD = CONFIG.hudHeight;
const TS = CONFIG.grid.tileSize;
const CW = CONFIG.canvas.width;
const CH = CONFIG.canvas.height;

/** `localStorage` keys gating the one-shot first-run aids. */
const KEY_SEEN_INTRO = 'overvolt.seenIntro';
const KEY_SEEN_TUTORIAL = 'overvolt.seenTutorial';

/** Read a persisted boolean flag; storage failures read back as `false`. */
function loadFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

/** Persist a boolean flag; storage failures are non-fatal and ignored. */
function saveFlag(key: string): void {
  try {
    localStorage.setItem(key, '1');
  } catch {
    // Best-effort only — onboarding still works for the current session.
  }
}

/**
 * The five wave-1 contextual hints (docs/GAME_DESIGN.md §12B), plus the
 * Sprint-25 `role` channel used for first-placement role-tour toasts. The
 * `role` toasts share the same renderer / lifetime / queue plumbing as the
 * wave-1 hints, but are not part of the `fired` one-shot set — they gate on
 * the per-profile `seenRoleHint` map (see {@link Onboarding.noteFirstPlacement}).
 */
type HintId = 'build' | 'placed' | 'wave1' | 'select' | 'brownout' | 'role';

/** An event that auto-dismisses a hint toast early. */
type DismissEvent = 'placement' | 'startwave' | 'select' | 'pylon' | null;

/** A live contextual-hint toast. */
interface Toast {
  hint: HintId;
  text: string;
  /** Wall-clock birth time, seconds. */
  born: number;
  /** Wall-clock time the toast disappears (brought forward by its event). */
  expireAt: number;
  /** Event that dismisses this toast early, or null for time-only. */
  dismissOn: DismissEvent;
}

/** The five one-shot wave-1 contextual hints — drives {@link Onboarding.fire}. */
type WaveHintId = Exclude<HintId, 'role'>;

/**
 * Static lifetimes + dismiss events for the wave-1 hint toasts. The copy is
 * resolved live through `t('hints.<id>')` at fire time so a locale switch
 * mid-session works without re-instantiating the onboarding layer.
 */
const HINT_META: Record<WaveHintId, { life: number; dismissOn: DismissEvent }> = {
  build: { life: 8, dismissOn: 'placement' },
  placed: { life: 8, dismissOn: 'startwave' },
  wave1: { life: 6, dismissOn: null },
  select: { life: 8, dismissOn: 'select' },
  brownout: { life: 8, dismissOn: 'pylon' },
};

/**
 * One-line role text per tower — docs/GAME_DESIGN.md §12C / STRATEGY_ANALYSIS.md
 * §5 / §7 REC 1+2. Each line names what *placement and situation* the tower
 * shines in, not just its stats — so a player can read its job off the
 * tooltip instead of reverse-engineering it from a fight. The five weapons now
 * each have a genuinely different job after the Sprint 13–15 re-rolings, and
 * these lines make that legible (REC 7, Sprint 18).
 */
/**
 * One-line role text per tower — resolved live through `t('towers.<type>.role')`
 * so a locale switch updates every tooltip / Help roster line without rebuild.
 * The English source-of-truth lives in `src/i18n/en.ts`; mirrors translate.
 */
function towerRole(type: TowerType): string {
  return t(`towers.${type}.role`);
}

/**
 * One-line gimmick per Glitch, for the Help roster. Sprint 69
 * (i18n-gaps-extract-s69) — resolved live through `t('enemyRole.<type>')` so a
 * locale switch updates the Help roster without rebuild. The English defaults
 * live in `src/i18n/en.ts:enemyRole`.
 */
function enemyRoleText(type: EnemyType): string {
  return t(`enemyRole.${type}`);
}

/**
 * One-time first-placement role-tour copy — Sprint 25 (tutorial-s25). The
 * Sprint-13–17 depth keystone silently re-roled four of the weapon towers,
 * and the Sprint-18 clarity pass surfaced the new roles in hover tooltips
 * — but a player who never hovers still discovers nothing. These toasts
 * pop **the first time the player places each type**, naming the role in
 * the imperative ("Align with...", "Place BACK from...") so the placement
 * lesson lands at the moment it's actionable.
 *
 * Copy is intentionally tighter than the tooltip {@link TOWER_ROLE} lines —
 * a toast has ~440px and ~5s, so it leads with the role label and the one
 * actionable placement rule. Wording is locked to STRATEGY_ANALYSIS.md §5
 * / Sprint-13–18 vocabulary so the player sees the same nouns everywhere
 * (chokepoint painter, line-of-fire pierce, indirect fire, power surplus).
 *
 * `null` means "skip this type" — Pulse has no surprising placement rule
 * (it's the neutral anchor, STRATEGY §5), but we still fire a brief
 * confirmation so the tour reads consistently across all six weapon types.
 */
/**
 * One-time first-placement role-tour copy per tower type — resolved live
 * through `t('roleHints.<type>')`. Returns `null` for "skip this type" —
 * historically Pulse was a no-op, but copy now exists for every type so the
 * resolution is always non-null. The function is kept for forward-compat
 * (a future locale could deliberately blank a hint by mapping to '').
 */
function roleHintText(type: TowerType): string | null {
  const text = t(`roleHints.${type}`);
  return text.length > 0 ? text : null;
}

/** Seconds a role-hint toast lives before auto-dismiss (Sprint 25). */
const ROLE_HINT_LIFE = 5;

/**
 * Sprint 53 (tutorial-flow-s53) — one-time **context-hint** toast copy,
 * keyed by {@link ContextHintId}. Each one fires once per profile on a
 * specific player-action edge (Overload dial past 0, wave 3 first reached,
 * Endless entered, puzzle-chip hover) — see `Onboarding.fireContextHint`
 * and the Game-side hooks that route to each public note method.
 *
 * Copy is leading-edge: the hint identifies the unfamiliar concept and
 * names the actionable lesson in one sentence (mirrors the Sprint-25
 * role-tour wording style — "what + why now").
 */
/** Live lookup for one of the four Sprint-53 context hint toasts (`t()`-backed). */
function contextHintText(id: ContextHintId): string {
  return t(`contextHints.${id}`);
}

/** Seconds a context-hint toast lives before auto-dismiss (Sprint 53). */
const CONTEXT_HINT_LIFE = 5;

/**
 * Sprint 53 — title-screen puzzle-chip hover region. The puzzle-mode chip
 * itself is not yet rendered as part of the RUN-MODE module (the chip lives
 * in the dormant `puzzleSelect.ts` overlay — see PUZZLE_DESIGN.md §puzzle
 * MVP scope); this rectangle mirrors the natural fourth-column slot to the
 * right of `MODE_MAZE_RECT` so the hover trigger is in the same row as the
 * existing Campaign / Endless / Maze chips. When a future sprint adds the
 * visible chip, the hover trigger lands directly on it without changes
 * here. Coordinates are canvas-space pixels, tested only on the wave-0
 * title screen.
 */
const PUZZLE_CHIP_HOVER_RECT = { x: 452, y: 202, w: 92, h: 30 } as const;

/** Display order for the Help enemy roster (phase-in order). */
const ENEMY_ORDER: readonly EnemyType[] = [
  'bit',
  'packet',
  'daemon',
  'fork',
  'cipher',
  'patch',
  'buffer',
  'streak',
];

/** Display order for the Help tower roster (hotkey order 1·2·3·4·5·6). */
const TOWER_ORDER: readonly TowerType[] = ['pulse', 'arc', 'pylon', 'cryo', 'mortar', 'sniper'];

/**
 * One-line role per active ability — surfaces *what the ability is for*, not
 * just the cost / cooldown. Users reported never using Airstrike / EMP Burst
 * because they couldn't tell what they did; the Help panel and the wave-phase
 * ability-slot hover tooltip both consume this (REC 7, Sprint 18).
 */
/**
 * One-line role per active ability — Sprint 69 (i18n-gaps-extract-s69) routes
 * this through `t('abilityRole.<id>')` so a locale switch updates the Help
 * roster + ability tooltip without rebuild.
 */
function abilityRoleText(id: AbilityId): string {
  return t(`abilityRole.${id}`);
}

/** Display order for the Help ability roster (slot keys Q · W · E). */
const ABILITY_ORDER: readonly AbilityId[] = ['overcharge', 'airstrike', 'empBurst'];

/** Per-ability accent — mirrors src/ui/hud.ts so colours stay aligned. */
const ABILITY_COLOR: Record<AbilityId, string> = {
  overcharge: '#2de2e6',
  airstrike: '#ff8c42',
  empBurst: '#bfe6ff',
};

/**
 * The four lines of body copy on the intro overlay (docs/GAME_DESIGN.md §12A) —
 * pulled live from the i18n catalogue so a locale change updates the modal.
 */
function introLines(): readonly string[] {
  return [t('intro.line1'), t('intro.line2'), t('intro.line3'), t('intro.line4')];
}

/** Help-panel layout rectangle. */
const HELP_PANEL = { x: 40, y: 24, w: 720, h: 524 };
/** "Replay intro" button inside the Help panel. */
const HELP_REPLAY = {
  x: HELP_PANEL.x + HELP_PANEL.w / 2 - 96,
  y: HELP_PANEL.y + HELP_PANEL.h - 44,
  w: 192,
  h: 28,
};
/** Help-panel close `X`, top-right. */
const HELP_CLOSE = { x: HELP_PANEL.x + HELP_PANEL.w - 34, y: HELP_PANEL.y + 12, w: 22, h: 22 };

/** The intro overlay's "Got it" dismiss button. */
const INTRO_BTN = { w: 168, h: 38 };

/** A pointer position in canvas pixels. */
interface Pointer {
  x: number;
  y: number;
}

/**
 * Owns all onboarding UI state and rendering. One instance lives on `Game`,
 * which forwards input and a per-frame `update` / `render`.
 */
export class Onboarding {
  private seenIntro: boolean;
  private seenTutorial: boolean;
  /** The intro overlay was dismissed this session. */
  private introDismissed = false;
  /** The intro is being replayed from the Help panel. */
  private introReplay = false;
  /** The Help panel is open. */
  private helpActive = false;

  /** Wave-1 hints that have already fired (one-shot each, per session). */
  private readonly fired = new Set<WaveHintId>();
  /** Live hint toasts. */
  private toasts: Toast[] = [];

  // --- per-frame edge-detection trackers ------------------------------------
  private prevPhase: GameState['phase'] = 'build';
  private prevTowers = 0;
  private prevSelected: number | null = null;
  private prevHadPylon = false;
  /** Sprint 53 — previous run mode, for the endless-entered edge trigger. */
  private prevMode: GameState['mode'] = 'campaign';
  /** Sprint 53 — was the cursor inside the puzzle-chip hover region last frame? */
  private prevPuzzleHover = false;

  constructor() {
    this.seenIntro = loadFlag(KEY_SEEN_INTRO);
    this.seenTutorial = loadFlag(KEY_SEEN_TUTORIAL);
  }

  /** True while the intro overlay should be shown. */
  private introVisible(): boolean {
    if (this.introReplay) return true;
    return CONFIG.onboardingEnabled && !this.seenIntro && !this.introDismissed;
  }

  /** True while a modal (intro or Help) is capturing input. */
  get modalOpen(): boolean {
    return this.helpActive || this.introVisible();
  }

  /** True while the Help panel is open (the renderer-side sim pause gate). */
  get helpOpen(): boolean {
    return this.helpActive;
  }

  /** Open the Help panel. */
  openHelp(): void {
    this.helpActive = true;
  }

  /** Close the Help panel. */
  private closeHelp(): void {
    this.helpActive = false;
  }

  /** Dismiss the intro overlay; the first dismissal persists `seenIntro`. */
  private dismissIntro(): void {
    if (!this.seenIntro) {
      this.seenIntro = true;
      saveFlag(KEY_SEEN_INTRO);
    }
    this.introDismissed = true;
    this.introReplay = false;
  }

  // --- input ----------------------------------------------------------------

  /**
   * Handle a key while a modal is open. `key` is `escape` / `h` / `?` when one
   * of those was pressed, else the sentinel `any`.
   */
  handleKey(key: string): void {
    if (this.introVisible()) {
      this.dismissIntro();
      return;
    }
    if (this.helpActive && (key === 'escape' || key === 'h' || key === '?')) {
      this.closeHelp();
    }
  }

  /** Handle a click while a modal is open. */
  handleClick(x: number, y: number): void {
    if (this.introVisible()) {
      this.dismissIntro();
      return;
    }
    if (this.helpActive) {
      if (inBox(HELP_REPLAY, x, y)) {
        this.closeHelp();
        this.introReplay = true;
      } else {
        // The close `X` and any click elsewhere both close the panel.
        this.closeHelp();
      }
    }
  }

  // --- per-frame update -----------------------------------------------------

  /**
   * Evaluate hint triggers and age toasts. Reads `state`; never mutates it.
   * `profile` is consulted so the Sprint-53 context-hint triggers (wave≥3,
   * endless entered, puzzle hover) can mark their one-shot flags; `pointer`
   * drives the puzzle-chip hover detection. The simulation is untouched —
   * this is a UI-only side-effect path (mirrors `noteFirstPlacement`).
   */
  update(state: GameState, profile: Profile, pointer: Pointer): void {
    const time = performance.now() / 1000;

    const hasPylon = state.towers.some((t) => t.type === 'pylon');
    const placed = state.towers.length > this.prevTowers;
    const started = this.prevPhase !== 'wave' && state.phase === 'wave';
    const selected = this.prevSelected === null && state.selectedTowerId !== null;
    const pyloned = !this.prevHadPylon && hasPylon;

    // Early-dismiss live toasts whose triggering event just happened.
    for (const t of this.toasts) {
      const hit =
        (t.dismissOn === 'placement' && placed) ||
        (t.dismissOn === 'startwave' && started) ||
        (t.dismissOn === 'select' && selected) ||
        (t.dismissOn === 'pylon' && pyloned);
      if (hit) t.expireAt = Math.min(t.expireAt, time + 0.35);
    }
    this.toasts = this.toasts.filter((t) => time < t.expireAt);

    // Fire contextual hints — gated by config + the localStorage flag, and held
    // back until the intro overlay has been dismissed. Maze Mode suppresses
    // them: their copy assumes a fixed path (docs/GAME_DESIGN.md §12).
    if (
      CONFIG.onboardingEnabled &&
      !this.seenTutorial &&
      !this.introVisible() &&
      state.mode !== 'maze'
    ) {
      const towers = state.towers.length;
      if (state.phase === 'build' && state.wave === 0 && towers === 0) {
        this.fire('build', time);
      }
      if (placed && this.prevTowers === 0) {
        this.fire('placed', time);
      }
      if (started && state.wave === 1) {
        this.fire('wave1', time);
      }
      if (state.phase === 'build' && state.wave === 1 && towers >= 1) {
        this.fire('select', time);
      }
      if (state.power.efficiency < 1) {
        this.fire('brownout', time);
      }
      // The tutorial is complete once every hint has fired or wave 2 begins.
      if (this.fired.size >= 5 || state.wave >= 2) {
        this.seenTutorial = true;
        saveFlag(KEY_SEEN_TUTORIAL);
      }
    }

    // Sprint 53 (tutorial-flow-s53) — context-hint edges. Gated only on
    // `CONFIG.onboardingEnabled` to match {@link noteFirstPlacement}'s
    // queue-then-let-render-decide convention: the toast layer already
    // hides queued toasts under the intro / Help modal, so a flag set
    // here while the intro is still up is harmless (the toast just won't
    // be visible, and the per-id one-shot flag prevents a re-fire). In
    // real play every trigger naturally happens after the intro is
    // dismissed (Overload / wave≥3 / Endless / puzzle-hover all need a
    // pre-existing run / pre-existing intent), so the practical UX is
    // unchanged.
    if (CONFIG.onboardingEnabled) {
      // P-key hint — first time the run reaches wave 3 (the player has at
      // minimum cleared waves 1–2, so the unlock-tree pitch lands at a moment
      // they can actually act on). `fireContextHint` is a no-op once the flag
      // is set, so calling it every frame is safe and self-deduplicating.
      if (state.wave >= 3) {
        this.fireContextHint(profile, 'profileKey');
      }
      // Endless-entered hint — fires whenever `state.mode` transitions to
      // `'endless'` (either Continue → Endless after a Campaign win, or a
      // pre-run select on the title chip). The prev-mode tracker keeps it
      // edge-triggered, but the seen-flag short-circuit makes that belt +
      // braces — both must agree, and the flag wins across sessions.
      if (state.mode === 'endless' && this.prevMode !== 'endless') {
        this.fireContextHint(profile, 'endlessMod');
      }
      // Puzzle-chip hover hint — only meaningful on the wave-0 title screen
      // (`isTitleScreen` mirrors hud.ts / Game.ts), and only on the
      // enter-region edge so a parked cursor doesn't spam-fire. The chip
      // region is forward-compatible — see {@link PUZZLE_CHIP_HOVER_RECT}.
      const titleScreen = state.phase === 'build' && state.wave === 0;
      const hoveringPuzzle = titleScreen && inHoverRect(PUZZLE_CHIP_HOVER_RECT, pointer);
      if (hoveringPuzzle && !this.prevPuzzleHover) {
        this.fireContextHint(profile, 'puzzleHover');
      }
      this.prevPuzzleHover = hoveringPuzzle;
    } else {
      this.prevPuzzleHover = false;
    }

    this.prevPhase = state.phase;
    this.prevTowers = state.towers.length;
    this.prevSelected = state.selectedTowerId;
    this.prevHadPylon = hasPylon;
    this.prevMode = state.mode;
  }

  /** Spawn a wave-1 hint toast once. */
  private fire(hint: WaveHintId, time: number): void {
    if (this.fired.has(hint)) return;
    this.fired.add(hint);
    const meta = HINT_META[hint];
    this.toasts.push({
      hint,
      text: t(`hints.${hint}`),
      born: time,
      expireAt: time + meta.life,
      dismissOn: meta.dismissOn,
    });
  }

  /**
   * Note the player's first placement of `type` on this profile, popping the
   * role-tour hint toast for that tower type **once per profile** — Sprint 25
   * (tutorial-s25). The seen flag persists on the profile (a parallel of the
   * `overvolt.seenIntro` pattern), so the toast never re-fires on subsequent
   * placements or across reloads. Multiple types placed in quick succession
   * queue and stack via the existing toast renderer — no special ordering.
   *
   * Gated by `CONFIG.onboardingEnabled` to match the rest of the §12 layer
   * (automated runs disable onboarding via `bootGame`). Never pauses the sim
   * or blocks input — toasts are non-blocking by construction.
   */
  noteFirstPlacement(profile: Profile, type: TowerType): void {
    if (!CONFIG.onboardingEnabled) return;
    if (profile.seenRoleHint[type]) return;
    const text = roleHintText(type);
    // Mark the flag even when copy is `null` so a "no hint" type doesn't get
    // re-checked every placement — but only persist on a real toast spawn so
    // disabled-onboarding runs leave the profile untouched.
    profile.seenRoleHint[type] = true;
    saveProfile(profile);
    if (text === null) return;
    const time = performance.now() / 1000;
    this.toasts.push({
      hint: 'role',
      text,
      born: time,
      expireAt: time + ROLE_HINT_LIFE,
      dismissOn: null,
    });
  }

  /**
   * Sprint 53 (tutorial-flow-s53) — note the player has cranked the Overload
   * dial past tier 0 for the first time on this profile, firing the Overload
   * context hint. Pre-tier-0 calls (the default dial position) are no-ops;
   * subsequent crank-ups are no-ops via the seen-flag short-circuit, so a
   * player dialing the tier up and down repeatedly never re-triggers.
   *
   * Gated by `CONFIG.onboardingEnabled` to match the rest of the §12 layer —
   * automated runs disable onboarding via `bootGame`, so this is a quiet
   * no-op there and leaves the profile untouched.
   */
  noteOverloadDial(profile: Profile, tier: number): void {
    if (tier <= 0) return;
    this.fireContextHint(profile, 'overload');
  }

  /**
   * Sprint 53 — internal helper for the four context-hint triggers. Marks the
   * one-shot flag on the profile, persists it, and queues a 5s non-blocking
   * toast that shares the role-tour renderer / lifetime / queue plumbing.
   * Gated identically to {@link noteFirstPlacement} on
   * `CONFIG.onboardingEnabled`, so disabled-onboarding runs (the automated
   * default) never write to the profile.
   *
   * The seen-flag short-circuit means every public call-site can fire each
   * frame without de-bouncing — the first matching trigger wins, the rest are
   * cheap no-ops (one map lookup, no allocation).
   */
  private fireContextHint(profile: Profile, id: ContextHintId): void {
    if (!CONFIG.onboardingEnabled) return;
    if (profile.seenContextHint[id]) return;
    profile.seenContextHint[id] = true;
    saveProfile(profile);
    const time = performance.now() / 1000;
    this.toasts.push({
      // Reuse the 'role' renderer channel — same cyan accent, same neon
      // pill, same lifetime envelope as the Sprint-25 role-tour toasts.
      hint: 'role',
      text: contextHintText(id),
      born: time,
      expireAt: time + CONTEXT_HINT_LIFE,
      dismissOn: null,
    });
  }

  // --- rendering ------------------------------------------------------------

  /** Draw every onboarding layer. Called once per frame, after the HUD. */
  render(
    ctx: CanvasRenderingContext2D,
    state: GameState,
    profile: Profile,
    pointer: Pointer,
  ): void {
    const time = performance.now() / 1000;
    if (this.helpActive) {
      this.drawHelp(ctx, time);
      return;
    }
    if (this.introVisible()) {
      this.drawIntro(ctx, time);
      return;
    }
    // Sprint 76 (modal-occlusion-fix-s76) — toasts stack under the HUD
    // strip, occupying playfield rows 0-3. Fade them to 35% opacity while
    // the player is actively placing a tower so they don't visually block
    // the buildable tile under the cursor. The placement preview from
    // `renderGame` (range ring, dead-zone, pylon aura) remains fully
    // visible underneath. The toasts are still readable as ghost text —
    // dismissing them outright would lose tutorial copy mid-fade.
    const armed = state.selectedTowerType !== null;
    if (armed) {
      ctx.save();
      ctx.globalAlpha = 0.35;
    }
    this.drawToasts(ctx, time);
    if (armed) ctx.restore();
    this.drawTooltip(ctx, state, profile, pointer);
  }

  // --- A. intro overlay -----------------------------------------------------

  /** The first-run intro: a modal card over the title screen. */
  private drawIntro(ctx: CanvasRenderingContext2D, time: number): void {
    ctx.save();
    ctx.fillStyle = 'rgba(5, 8, 16, 0.9)';
    ctx.fillRect(0, 0, CW, CH);

    const w = 540;
    const h = 364;
    const x = (CW - w) / 2;
    const y = (CH - h) / 2;
    neonPanel(ctx, x, y, w, h, C.accent, time);

    const cx = CW / 2;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';

    // Eyebrow + title.
    ctx.font = `10px ${FONT_MONO}`;
    ctx.fillStyle = withAlpha(C.accent, 0.7);
    ctx.fillText(t('intro.eyebrow'), cx, y + 36);
    glowText(ctx, t('intro.titleBig'), cx, y + 74, `700 38px ${FONT_DISPLAY}`, C.accent, 18);
    ctx.font = `600 14px ${FONT_DISPLAY}`;
    ctx.fillStyle = C.hudText;
    ctx.fillText(t('intro.subtitle'), cx, y + 96);

    // Body copy.
    ctx.strokeStyle = withAlpha(C.accent, 0.25);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x + 40, y + 112);
    ctx.lineTo(x + w - 40, y + 112);
    ctx.stroke();

    let ty = y + 138;
    ctx.font = `12px ${FONT_MONO}`;
    const bodyLines = introLines();
    for (let i = 0; i < bodyLines.length; i += 1) {
      const accent = i === bodyLines.length - 1;
      ctx.fillStyle = accent ? withAlpha(C.accent, 0.92) : C.hudText;
      const lines = wrapText(ctx, bodyLines[i]!, w - 96);
      for (const line of lines) {
        ctx.fillText(line, cx, ty);
        ty += 17;
      }
      ty += 8;
    }

    // "Got it" button.
    const btnX = cx - INTRO_BTN.w / 2;
    const btnY = y + h - 58;
    const pulse = 0.6 + 0.4 * Math.sin(time * 3);
    ctx.fillStyle = withAlpha(C.accent, 0.16 + 0.1 * pulse);
    roundRect(ctx, btnX, btnY, INTRO_BTN.w, INTRO_BTN.h, 8);
    ctx.fill();
    ctx.strokeStyle = withAlpha(C.accent, 0.6 + 0.35 * pulse);
    ctx.lineWidth = 1.7;
    ctx.shadowBlur = 12 * pulse;
    ctx.shadowColor = C.accent;
    roundRect(ctx, btnX, btnY, INTRO_BTN.w, INTRO_BTN.h, 8);
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.font = `700 14px ${FONT_DISPLAY}`;
    ctx.fillStyle = C.accent;
    ctx.textBaseline = 'middle';
    ctx.fillText(t('intro.gotIt'), cx, btnY + INTRO_BTN.h / 2 + 1);
    ctx.textBaseline = 'alphabetic';
    ctx.font = `9px ${FONT_MONO}`;
    ctx.fillStyle = C.hudMuted;
    ctx.fillText(t('intro.gotItHint'), cx, btnY + INTRO_BTN.h + 16);
    ctx.restore();
  }

  // --- B. contextual hint toasts -------------------------------------------

  /** Draw the live, non-blocking hint toasts stacked under the HUD strip. */
  private drawToasts(ctx: CanvasRenderingContext2D, time: number): void {
    let y = HUD + 14;
    for (const t of this.toasts) {
      const age = time - t.born;
      const remain = t.expireAt - time;
      const fade = clamp(Math.min(age / 0.25, remain / 0.4), 0, 1);
      if (fade <= 0) continue;
      y += this.drawToast(ctx, t.text, y, fade) + 8;
    }
  }

  /** Draw one hint toast pill; returns its height. */
  private drawToast(
    ctx: CanvasRenderingContext2D,
    text: string,
    top: number,
    fade: number,
  ): number {
    ctx.save();
    ctx.globalAlpha = fade;
    ctx.font = `11px ${FONT_MONO}`;
    const maxW = 440;
    const lines = wrapText(ctx, text, maxW);
    const lineH = 15;
    const padX = 16;
    const padY = 9;
    let textW = 0;
    for (const line of lines) textW = Math.max(textW, ctx.measureText(line).width);
    const w = textW + padX * 2 + 22;
    const h = lines.length * lineH + padY * 2;
    const x = (CW - w) / 2;

    ctx.fillStyle = 'rgba(9, 14, 28, 0.95)';
    roundRect(ctx, x, top, w, h, 9);
    ctx.fill();
    ctx.strokeStyle = withAlpha(C.accent, 0.7);
    ctx.lineWidth = 1.2;
    ctx.shadowBlur = 10;
    ctx.shadowColor = withAlpha(C.accent, 0.6);
    roundRect(ctx, x, top, w, h, 9);
    ctx.stroke();
    ctx.shadowBlur = 0;

    // Left accent rail + a small "hint" bulb.
    ctx.fillStyle = C.accent;
    roundRect(ctx, x, top, 3, h, 1.5);
    ctx.fill();
    glowDot(ctx, x + 14, top + h / 2, 3.4, C.accent);

    ctx.fillStyle = C.hudText;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    let ly = top + padY + lineH / 2;
    for (const line of lines) {
      ctx.fillText(line, x + 30, ly);
      ly += lineH;
    }
    ctx.restore();
    return h;
  }

  // --- C. tower tooltips ----------------------------------------------------

  /**
   * Draw a tooltip when hovering a HUD tower button, a placed tower, or — in
   * wave phase — an ABILITIES-zone slot. Users reported never using locked
   * abilities (Airstrike / EMP Burst) because the slot offered no read-out of
   * *what they do*; the slot tooltip now names the role and the unlock path,
   * mirroring the locked-tower-chip pattern (REC 7, Sprint 18).
   *
   * Sprint 72 (ux-zorder-fix-s72) — Track A: when a tower is selected, the
   * contextual selection panel (Upgrade / Sell / Target / Overclock) is the
   * focused affordance and must not be occluded by a stale hover tooltip. A
   * tester at overvolt.quest reported that clicking a placed tower painted
   * the panel *behind* the still-on-screen tooltip from the previous hover,
   * forcing them to move the mouse away before they could click an action.
   * Tooltips re-render after the HUD, so they win the z-order race. We fix
   * by short-circuiting all hover tooltips while the selection panel is
   * live — the panel itself already names the tower (header) and exposes
   * level / cost via Upgrade / Sell rows, so no information is lost.
   */
  private drawTooltip(
    ctx: CanvasRenderingContext2D,
    state: GameState,
    profile: Profile,
    p: Pointer,
  ): void {
    // Sprint 72 — suppress tooltips while the selection panel owns the
    // playfield. Without this guard, hovering the same tower the player
    // just clicked re-paints the placed-tower tooltip on top of the panel
    // (panel is drawn by `drawHud`, tooltip is drawn after `drawHud` in
    // Game.render's order). Suppressing chip + ability tooltips too is the
    // simplest cut: while a tower is selected the player is in a modal
    // "decide what to do with this tower" loop, not a discovery loop.
    if (state.selectedTowerId !== null) return;
    // Sprint 76 (modal-occlusion-fix-s76) — suppress tooltips while a tower
    // is armed for placement (the player has clicked a chip or pressed
    // 1-7 and is about to drop the tower on a tile). The hover tooltip
    // grows ~244 × ~180 px and would otherwise paint over the buildable
    // tile under the cursor, obscuring exactly the tile the player is
    // aiming at. The tile placement preview (range ring, dead-zone ring,
    // pylon-aura preview) is drawn by `renderGame` and remains visible.
    if (state.selectedTowerType !== null) return;
    // BUILD-zone tower chip hover. The layout takes the run mode (Maze adds the
    // Wall chip) and the profile (so a locked chip's tooltip explains itself).
    const layout = hudLayout(state.mode, profile);
    for (const chip of layout.towerChips) {
      const b = chip.rect;
      if (p.x >= b.x && p.x <= b.x + b.w && p.y >= b.y && p.y <= b.y + b.h) {
        this.renderTooltip(ctx, chip.type, 1, b.x + b.w / 2, b.y + b.h + 8, 'below', chip.locked);
        return;
      }
    }
    // Wave-phase ability-slot hover. Slots only render during the wave (the
    // ABILITIES zone hosts the Overload dial in build phase), so gate likewise.
    if (state.phase === 'wave') {
      for (let i = 0; i < state.abilities.length; i += 1) {
        const slot = layout.abilitySlots[i];
        const ability = state.abilities[i];
        if (!slot || !ability) continue;
        if (p.x >= slot.x && p.x <= slot.x + slot.w && p.y >= slot.y && p.y <= slot.y + slot.h) {
          const locked = !isAbilityUnlocked(profile, ability.id);
          this.renderAbilityTooltip(
            ctx,
            ability.id,
            slot.x + slot.w / 2,
            slot.y + slot.h + 8,
            'below',
            locked,
          );
          return;
        }
      }
    }
    // Placed-tower hover (a placed tower is never locked).
    if (p.y <= HUD) return;
    const col = Math.floor(p.x / TS);
    const row = Math.floor((p.y - HUD) / TS);
    const tower = state.towers.find((t) => t.col === col && t.row === row);
    if (tower) {
      const tx = col * TS + TS / 2;
      const ty = HUD + row * TS;
      this.renderTooltip(ctx, tower.type, tower.level, tx, ty - 8, 'above');
    }
  }

  /**
   * Render a tooltip card for `type` at `level`. `anchorY` is the edge the card
   * grows away from: `below` grows down from it, `above` grows up from it. When
   * `locked` the card carries an unlock-path block — a real player reported not
   * knowing how to unlock gated towers like Mortar, so the tooltip names the
   * Flux cost and that the Profile screen (the `P` key) is where to spend it.
   */
  private renderTooltip(
    ctx: CanvasRenderingContext2D,
    type: TowerType,
    level: number,
    anchorX: number,
    anchorY: number,
    grow: 'above' | 'below',
    locked = false,
  ): void {
    const def = CONFIG.towers[type];
    const placed = level > 0 && grow === 'above';
    const stats = towerStats(type, level);
    const nextLevel = level < CONFIG.maxTowerLevel ? level + 1 : null;
    const showNext = placed && nextLevel !== null;
    const nextStats = showNext ? towerStats(type, nextLevel!) : [];

    ctx.save();
    ctx.font = `11px ${FONT_MONO}`;
    const w = 244;
    const roleLines = wrapText(ctx, towerRole(type), w - 24);
    const unlockLines = locked
      ? wrapText(
          ctx,
          t('hud.lockedTooltip', { cost: towerUnlockCost(type) }),
          w - 24,
        )
      : [];

    // Measure height.
    let h = 8 + 20 + 6; // pad + header + gap
    h += stats.length * 15 + 6;
    if (showNext) h += 16 + nextStats.length * 15 + 6;
    if (locked) h += 4 + unlockLines.length * 13 + 2; // unlock-path block
    h += 1 + 6 + roleLines.length * 14 + 8; // divider + role

    let x = clamp(anchorX - w / 2, 6, CW - w - 6);
    let y = grow === 'below' ? anchorY : anchorY - h;
    y = clamp(y, HUD + 4, CH - h - 4);

    // Card.
    ctx.fillStyle = 'rgba(10, 15, 30, 0.97)';
    roundRect(ctx, x, y, w, h, 8);
    ctx.fill();
    ctx.strokeStyle = withAlpha(def.color, 0.85);
    ctx.lineWidth = 1.3;
    ctx.shadowBlur = 14;
    ctx.shadowColor = withAlpha(def.color, 0.55);
    roundRect(ctx, x, y, w, h, 8);
    ctx.stroke();
    ctx.shadowBlur = 0;
    // Color rail.
    ctx.fillStyle = def.color;
    roundRect(ctx, x, y, w, 3, 1.5);
    ctx.fill();

    const ix = x + 12;
    let cy = y + 10;

    // Header: label + cost / level badge.
    ctx.textBaseline = 'top';
    ctx.textAlign = 'left';
    ctx.font = `700 13px ${FONT_DISPLAY}`;
    // Sprint 69 — route tower label through `t('towers.<type>.label')`.
    glowTextLeft(ctx, t(`towers.${type}.label`), ix, cy, def.color, 6);
    ctx.textAlign = 'right';
    ctx.font = `10px ${FONT_MONO}`;
    if (locked) {
      // Sprint 69 — reuse-bug fix: hud.locked existed in the catalogue.
      ctx.fillStyle = C.warning;
      ctx.fillText(t('hud.locked'), x + w - 12, cy + 2);
    } else {
      ctx.fillStyle = C.hudMuted;
      ctx.fillText(
        placed
          ? t('tooltip.levelOf', { level, max: CONFIG.maxTowerLevel })
          : t('tooltip.credits', { cost: def.cost }),
        x + w - 12,
        cy + 2,
      );
    }
    ctx.textAlign = 'left';
    cy += 24;

    // Current stats.
    ctx.font = `11px ${FONT_MONO}`;
    ctx.fillStyle = C.hudText;
    for (const line of stats) {
      ctx.fillText(line, ix, cy);
      cy += 15;
    }

    // Next-level preview (placed towers only).
    if (showNext) {
      cy += 4;
      ctx.fillStyle = withAlpha('#f7d51d', 0.95);
      ctx.font = `9px ${FONT_MONO}`;
      const cost = def.upgradeCosts[level - 1];
      ctx.fillText(t('tooltip.next', { level: nextLevel, cost: cost ?? '?' }), ix, cy);
      cy += 14;
      ctx.font = `11px ${FONT_MONO}`;
      ctx.fillStyle = withAlpha(C.hudText, 0.72);
      for (const line of nextStats) {
        ctx.fillText(line, ix, cy);
        cy += 15;
      }
    } else if (placed) {
      cy += 4;
      ctx.fillStyle = withAlpha('#f7d51d', 0.85);
      ctx.font = `9px ${FONT_MONO}`;
      // Sprint 69 — reuse-bug fix: hud.maxLevel already in catalogue.
      ctx.fillText(t('hud.maxLevel'), ix, cy);
      cy += 14;
    } else if (locked) {
      // Unlock-path block — names the Flux cost and the Profile screen / P key.
      cy += 4;
      ctx.font = `9px ${FONT_MONO}`;
      ctx.fillStyle = C.warning;
      for (const line of unlockLines) {
        ctx.fillText(line, ix, cy);
        cy += 13;
      }
      cy += 2;
    }

    // Role line.
    cy += 2;
    ctx.strokeStyle = withAlpha(def.color, 0.25);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(ix, cy);
    ctx.lineTo(x + w - 12, cy);
    ctx.stroke();
    cy += 7;
    ctx.font = `10px ${FONT_MONO}`;
    ctx.fillStyle = withAlpha(C.accent, 0.9);
    for (const line of roleLines) {
      ctx.fillText(line, ix, cy);
      cy += 14;
    }
    ctx.restore();
  }

  /**
   * Render a tooltip card for active ability `id` — name + cost, the
   * one-line role, and an unlock-path block when `locked`. The locked path
   * mirrors the tower-tooltip convention so the player learns the same way
   * (press P → Profile → spend Flux). See REC 7, Sprint 18.
   */
  private renderAbilityTooltip(
    ctx: CanvasRenderingContext2D,
    id: AbilityId,
    anchorX: number,
    anchorY: number,
    grow: 'above' | 'below',
    locked: boolean,
  ): void {
    const def = CONFIG.abilities.find((a) => a.id === id);
    if (!def) return;
    const color = ABILITY_COLOR[id];

    ctx.save();
    ctx.font = `11px ${FONT_MONO}`;
    const w = 244;
    const roleLines = wrapText(ctx, abilityRoleText(id), w - 24);
    const unlockLines = locked
      ? // Sprint 69 — reuse-bug fix: hud.lockedTooltip in the catalogue.
        wrapText(ctx, t('hud.lockedTooltip', { cost: abilityUnlockCost(id) }), w - 24)
      : [];

    // Measure height: pad + header + cost line + (locked block) + role.
    let h = 8 + 20 + 6;
    h += 15 + 6; // cost / cooldown line
    if (locked) h += 4 + unlockLines.length * 13 + 2;
    h += 1 + 6 + roleLines.length * 14 + 8;

    const x = clamp(anchorX - w / 2, 6, CW - w - 6);
    let y = grow === 'below' ? anchorY : anchorY - h;
    y = clamp(y, HUD + 4, CH - h - 4);

    // Card body, neon outline, color rail.
    ctx.fillStyle = 'rgba(10, 15, 30, 0.97)';
    roundRect(ctx, x, y, w, h, 8);
    ctx.fill();
    ctx.strokeStyle = withAlpha(color, 0.85);
    ctx.lineWidth = 1.3;
    ctx.shadowBlur = 14;
    ctx.shadowColor = withAlpha(color, 0.55);
    roundRect(ctx, x, y, w, h, 8);
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.fillStyle = color;
    roundRect(ctx, x, y, w, 3, 1.5);
    ctx.fill();

    const ix = x + 12;
    let cy = y + 10;

    // Header: name + LOCKED / cost badge.
    ctx.textBaseline = 'top';
    ctx.textAlign = 'left';
    ctx.font = `700 13px ${FONT_DISPLAY}`;
    // Sprint 69 — route ability label through `t('abilities.<id>.label')`.
    glowTextLeft(ctx, t(`abilities.${id}.label`), ix, cy, color, 6);
    ctx.textAlign = 'right';
    ctx.font = `10px ${FONT_MONO}`;
    if (locked) {
      // Sprint 69 — reuse-bug fix: hud.locked already in catalogue.
      ctx.fillStyle = C.warning;
      ctx.fillText(t('hud.locked'), x + w - 12, cy + 2);
    } else {
      ctx.fillStyle = C.hudMuted;
      ctx.fillText(t('tooltip.credits', { cost: def.cost }), x + w - 12, cy + 2);
    }
    ctx.textAlign = 'left';
    cy += 24;

    // Cooldown line — the only stat worth showing for an ability.
    ctx.font = `11px ${FONT_MONO}`;
    ctx.fillStyle = C.hudText;
    ctx.fillText(
      t('tooltip.cooldown', { sec: (def.cooldownTicks / CONFIG.tickRate).toFixed(1) }),
      ix,
      cy,
    );
    cy += 15;

    // Unlock-path block — same convention as the locked-tower tooltip.
    if (locked) {
      cy += 4;
      ctx.font = `9px ${FONT_MONO}`;
      ctx.fillStyle = C.warning;
      for (const line of unlockLines) {
        ctx.fillText(line, ix, cy);
        cy += 13;
      }
      cy += 2;
    }

    // Role line.
    cy += 2;
    ctx.strokeStyle = withAlpha(color, 0.25);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(ix, cy);
    ctx.lineTo(x + w - 12, cy);
    ctx.stroke();
    cy += 7;
    ctx.font = `10px ${FONT_MONO}`;
    ctx.fillStyle = withAlpha(C.accent, 0.9);
    for (const line of roleLines) {
      ctx.fillText(line, ix, cy);
      cy += 14;
    }
    ctx.restore();
  }

  // --- D. Help panel --------------------------------------------------------

  /** Draw the Help panel: controls, rosters, the power rule and objectives. */
  private drawHelp(ctx: CanvasRenderingContext2D, time: number): void {
    ctx.save();
    ctx.fillStyle = 'rgba(5, 8, 16, 0.9)';
    ctx.fillRect(0, 0, CW, CH);

    const { x, y, w, h } = HELP_PANEL;
    neonPanel(ctx, x, y, w, h, C.accent, time);

    // Title.
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.font = `9px ${FONT_MONO}`;
    ctx.fillStyle = withAlpha(C.accent, 0.7);
    ctx.fillText(t('help.eyebrow'), x + 28, y + 16);
    glowTextLeft(ctx, t('help.title'), x + 28, y + 28, C.accent, 12);
    ctx.font = `700 18px ${FONT_DISPLAY}`;

    // Close `X`.
    ctx.strokeStyle = withAlpha(C.bad, 0.85);
    ctx.lineWidth = 1.4;
    roundRect(ctx, HELP_CLOSE.x, HELP_CLOSE.y, HELP_CLOSE.w, HELP_CLOSE.h, 5);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(HELP_CLOSE.x + 7, HELP_CLOSE.y + 7);
    ctx.lineTo(HELP_CLOSE.x + HELP_CLOSE.w - 7, HELP_CLOSE.y + HELP_CLOSE.h - 7);
    ctx.moveTo(HELP_CLOSE.x + HELP_CLOSE.w - 7, HELP_CLOSE.y + 7);
    ctx.lineTo(HELP_CLOSE.x + 7, HELP_CLOSE.y + HELP_CLOSE.h - 7);
    ctx.stroke();

    const colL = x + 28;
    const colR = x + w / 2 + 14;
    const colW = w / 2 - 44;

    // Left column — controls, power grid, objective.
    let ly = y + 62;
    ly = section(ctx, t('help.controls'), colL, ly, colW);
    for (const [k, key] of CONTROL_ROWS) ly = controlRow(ctx, k, t(key), colL, ly, colW);
    ly += 10;
    ly = section(ctx, t('help.powerGrid'), colL, ly, colW);
    ly = bodyText(ctx, t('help.powerGridBody'), colL, ly, colW);
    ly += 10;
    ly = section(ctx, t('help.objective'), colL, ly, colW);
    ly = bodyText(ctx, t('help.objectiveBody'), colL, ly, colW);
    ly += 10;
    ly = section(ctx, t('help.mazeMode'), colL, ly, colW);
    ly = bodyText(ctx, t('help.mazeModeBody'), colL, ly, colW);

    // Right column — Glitch + tower rosters.
    let ry = y + 62;
    ry = section(ctx, t('help.glitches'), colR, ry, colW);
    for (const e of ENEMY_ORDER) {
      ry = rosterRow(
        ctx,
        CONFIG.enemies[e].color,
        t(`enemies.${e}.label`),
        enemyRoleText(e),
        colR,
        ry,
        colW,
      );
    }
    ry += 10;
    ry = section(ctx, t('help.towers'), colR, ry, colW);
    for (const towerType of TOWER_ORDER) {
      ry = rosterRow(
        ctx,
        CONFIG.towers[towerType].color,
        t(`towers.${towerType}.label`),
        towerRole(towerType),
        colR,
        ry,
        colW,
      );
    }
    // ABILITIES — surfaces *what* each active ability is for, so locked tools
    // (Airstrike / EMP Burst) read as a known reason to unlock, not a mystery.
    ry += 10;
    ry = section(ctx, t('help.abilities'), colR, ry, colW);
    for (const a of ABILITY_ORDER) {
      const def = CONFIG.abilities.find((d) => d.id === a);
      if (!def) continue;
      ry = rosterRow(
        ctx,
        ABILITY_COLOR[a],
        t(`abilities.${a}.label`),
        abilityRoleText(a),
        colR,
        ry,
        colW,
      );
    }

    // Replay-intro button + close hint.
    const r = HELP_REPLAY;
    const pulse = 0.6 + 0.4 * Math.sin(time * 3);
    ctx.fillStyle = withAlpha(C.accent, 0.14);
    roundRect(ctx, r.x, r.y, r.w, r.h, 7);
    ctx.fill();
    ctx.strokeStyle = withAlpha(C.accent, 0.5 + 0.35 * pulse);
    ctx.lineWidth = 1.5;
    roundRect(ctx, r.x, r.y, r.w, r.h, 7);
    ctx.stroke();
    ctx.font = `700 12px ${FONT_DISPLAY}`;
    ctx.fillStyle = C.accent;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(t('help.replayIntro'), r.x + r.w / 2, r.y + r.h / 2 + 1);
    ctx.font = `9px ${FONT_MONO}`;
    ctx.fillStyle = C.hudMuted;
    ctx.fillText(t('help.closeHint'), CW / 2, r.y + r.h + 13);
    ctx.restore();
  }
}

/**
 * Keyboard control rows for the Help panel. Sprint 69 (i18n-gaps-extract-s69)
 * routes the description column through `t('controls.<id>')` so a locale
 * switch updates the Help table without rebuild. Hotkey caps stay as raw
 * tokens (they map to physical keys, not natural language).
 */
const CONTROL_ROWS: ReadonlyArray<readonly [string, string]> = [
  ['1-6', 'controls.arm'],
  ['Mouse', 'controls.mouse'],
  ['Space', 'controls.space'],
  ['F', 'controls.fast'],
  ['U / S', 'controls.upgradeSell'],
  ['T', 'controls.target'],
  ['P', 'controls.profile'],
  ['Esc / M / R', 'controls.utility'],
];

// --- shared drawing helpers --------------------------------------------------

/** True when (x, y) lies in box `b`. */
function inBox(b: { x: number; y: number; w: number; h: number }, x: number, y: number): boolean {
  return x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h;
}

/** Sprint 53 — true when the pointer is inside the hover rectangle `r`. */
function inHoverRect(
  r: { x: number; y: number; w: number; h: number },
  p: Pointer,
): boolean {
  return p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h;
}

/** A neon dialog panel with a soft glow and corner brackets. */
function neonPanel(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  color: string,
  time: number,
): void {
  const g = ctx.createLinearGradient(0, y, 0, y + h);
  g.addColorStop(0, 'rgba(14, 22, 42, 0.98)');
  g.addColorStop(1, 'rgba(8, 12, 24, 0.98)');
  ctx.fillStyle = g;
  roundRect(ctx, x, y, w, h, 12);
  ctx.fill();
  ctx.strokeStyle = withAlpha(color, 0.55 + 0.22 * Math.sin(time * 2.5));
  ctx.lineWidth = 1.5;
  ctx.shadowBlur = 24;
  ctx.shadowColor = color;
  roundRect(ctx, x, y, w, h, 12);
  ctx.stroke();
  ctx.shadowBlur = 0;
  ctx.strokeStyle = color;
  ctx.lineWidth = 2.5;
  const k = 18;
  const corner = (px: number, py: number, sx: number, sy: number): void => {
    ctx.beginPath();
    ctx.moveTo(px + sx * k, py);
    ctx.lineTo(px, py);
    ctx.lineTo(px, py + sy * k);
    ctx.stroke();
  };
  corner(x + 7, y + 7, 1, 1);
  corner(x + w - 7, y + 7, -1, 1);
  corner(x + 7, y + h - 7, 1, -1);
  corner(x + w - 7, y + h - 7, -1, -1);
}

/** A small section heading with an underline rule; returns the next y. */
function section(
  ctx: CanvasRenderingContext2D,
  label: string,
  x: number,
  y: number,
  w: number,
): number {
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.font = `10px ${FONT_MONO}`;
  ctx.fillStyle = C.accent;
  ctx.fillText(spaced(label), x, y);
  ctx.strokeStyle = withAlpha(C.accent, 0.3);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x, y + 16);
  ctx.lineTo(x + w, y + 16);
  ctx.stroke();
  return y + 25;
}

/** One keyboard-control row (key chip + description); returns the next y. */
function controlRow(
  ctx: CanvasRenderingContext2D,
  key: string,
  label: string,
  x: number,
  y: number,
  w: number,
): number {
  ctx.font = `9px ${FONT_MONO}`;
  const kw = Math.max(ctx.measureText(key).width + 12, 30);
  ctx.fillStyle = '#101a30';
  roundRect(ctx, x, y, kw, 16, 4);
  ctx.fill();
  ctx.strokeStyle = withAlpha(C.accent, 0.55);
  ctx.lineWidth = 1;
  roundRect(ctx, x, y, kw, 16, 4);
  ctx.stroke();
  ctx.fillStyle = C.accent;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(key, x + kw / 2, y + 9);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.font = `11px ${FONT_MONO}`;
  ctx.fillStyle = C.hudText;
  const lines = wrapText(ctx, label, w - kw - 12);
  let ly = y + 2;
  for (const line of lines) {
    ctx.fillText(line, x + kw + 10, ly);
    ly += 14;
  }
  return Math.max(y + 20, ly + 4);
}

/** A wrapped paragraph of muted body text; returns the next y. */
function bodyText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  w: number,
): number {
  ctx.font = `11px ${FONT_MONO}`;
  ctx.fillStyle = withAlpha(C.hudText, 0.82);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  let ly = y;
  for (const line of wrapText(ctx, text, w)) {
    ctx.fillText(line, x, ly);
    ly += 15;
  }
  return ly;
}

/** One roster row: a glowing dot, a bold name and a one-line role. */
function rosterRow(
  ctx: CanvasRenderingContext2D,
  color: string,
  name: string,
  role: string,
  x: number,
  y: number,
  w: number,
): number {
  glowDot(ctx, x + 5, y + 7, 4.5, color);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.font = `700 11px ${FONT_DISPLAY}`;
  ctx.fillStyle = color;
  ctx.fillText(name, x + 16, y);
  const nameW = ctx.measureText(name).width;
  ctx.font = `10px ${FONT_MONO}`;
  ctx.fillStyle = withAlpha(C.hudText, 0.78);
  const roleX = x + 16 + nameW + 8;
  const lines = wrapText(ctx, role, x + w - roleX);
  let ly = y + 1;
  for (const line of lines) {
    ctx.fillText(line, roleX, ly);
    ly += 13;
  }
  return Math.max(y + 19, ly + 5);
}

/** A crisp dot wrapped in a soft glow. */
function glowDot(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, color: string): void {
  ctx.save();
  ctx.shadowBlur = 8;
  ctx.shadowColor = color;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** Centre-aligned text with a glow. */
function glowText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  font: string,
  color: string,
  glow: number,
): void {
  ctx.save();
  ctx.font = font;
  ctx.textAlign = 'center';
  ctx.fillStyle = color;
  ctx.shadowBlur = glow;
  ctx.shadowColor = color;
  ctx.fillText(text, x, y);
  ctx.restore();
}

/** Left-aligned, top-baseline text with a glow. */
function glowTextLeft(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  color: string,
  glow: number,
): void {
  ctx.save();
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillStyle = color;
  ctx.shadowBlur = glow;
  ctx.shadowColor = color;
  ctx.fillText(text, x, y);
  ctx.restore();
}

/** Letter-space a short label for the diagnostics look. */
function spaced(s: string): string {
  return s.split('').join(' ');
}

/** Greedily wrap `text` to lines no wider than `maxWidth` (ctx font preset). */
function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(candidate).width > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/** Build the stat lines shown in a tooltip for `type` at `level`. */
function towerStats(type: TowerType, level: number): string[] {
  const def = CONFIG.towers[type];
  const lvl = def.levels[level - 1]!;
  if (type === 'pylon') {
    return [t('tooltip.stat.supply', { n: (lvl as PylonLevel).supply })];
  }
  const cd = (lvl.cooldownTicks / CONFIG.tickRate).toFixed(1);
  const lines = [
    t('tooltip.stat.dmgRngCd', { damage: lvl.damage, range: lvl.range, cd }),
  ];
  if (type === 'arc') {
    lines.push(
      t('tooltip.stat.pwrChain', {
        power: lvl.powerDraw,
        chain: (lvl as ArcLevel).chainTargets,
      }),
    );
  } else if (type === 'cryo') {
    const cl = lvl as CryoLevel;
    lines.push(
      t('tooltip.stat.pwrAoe', { power: lvl.powerDraw, targets: cl.aoeTargets }),
    );
    lines.push(
      t('tooltip.stat.slow', {
        factor: cl.slowFactor,
        sec: (cl.slowDuration / CONFIG.tickRate).toFixed(1),
      }),
    );
    // Sprint 17 brittle mark — the +x% damage uplift other towers get on a
    // painted enemy. Round to a whole percent for tooltip readability.
    const britPct = Math.round((cl.brittleAmp - 1) * 100);
    lines.push(t('tooltip.stat.brittle', { pct: britPct }));
  } else if (type === 'mortar') {
    lines.push(
      t('tooltip.stat.pwrBlast', {
        power: lvl.powerDraw,
        tiles: (lvl as MortarLevel).blastRadius,
      }),
    );
  } else {
    lines.push(t('tooltip.stat.pwrSingle', { power: lvl.powerDraw }));
  }
  return lines;
}
